// src/pages/SettingsPage.js

import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useSnackbar } from "notistack";
import {
  Tooltip,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
} from "@mui/material";
import { Switch, Card, CardHeader } from '@ui5/webcomponents-react';
import httpRequest from "../../httpRequest";
import AppLayout from "../../components/AppLayout/AppLayout";
import RolesCard from "../../components/RolesCard/RolesCard";
import CompanyTable from "../../components/CompanyTable/CompanyTable";
import FunctionsCard from "../../components/FunctionsCard/FunctionsCard";
import MailboxCard from "../../components/MailboxCard/MailboxCard";
import PositionsCard from "../../components/PositionsCard/PositionsCard";
import CategoriesCard from "../../components/CategoriesCard/CategoriesCard";
import ExpenseHierarchyCard from "../../components/ExpenseHierarchyCard/ExpenseHierarchyCard";
import EmployeesCard from "../../components/EmployeesCard/EmployeesCard";
import SmtpCard from "../../components/SmtpCard/SmtpCard";
import TemplatesCard from "../../components/TemplatesCard/TemplatesCard";
import ButtonContained from "../../components/StyledComponents/ButtonContained";
import ButtonOutlined from "../../components/StyledComponents/ButtonOutlined";

const SettingsPage = () => {
  const [quickScan, setQuickScan] = useState(false);
  const [businessTransaction, setBusinessTransaction] = useState(false);
  const [roles, setRoles] = useState([]);
  const [refresh, setRefresh] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newBusinessTransactionValue, setNewBusinessTransactionValue] = useState(false);
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();

  // DMS Settings State
  const [dmsUrl, setDmsUrl] = useState('');
  const [apiKey, setApiKey] = useState('');

  // Fetch Quick Scan Setting
  useEffect(() => {
    const fetchQuickScanSetting = async () => {
      try {
        const resp = await httpRequest.get(`${process.env.REACT_APP_BACKEND_URL}/get_quick_scan`);
        setQuickScan(resp.data.QuickScan === 'true');
      } catch (error) {
        console.error("Error fetching QuickScan setting:", error);
      }
    };

    fetchQuickScanSetting();
  }, []);

  // Fetch Business Transaction Setting
  useEffect(() => {
    const fetchBusinessTransactionSetting = async () => {
      try {
        const resp = await httpRequest.get(`${process.env.REACT_APP_BACKEND_URL}/get_business_transaction`);
        setBusinessTransaction(resp.data.BusinessTransaction === 'true');
      } catch (error) {
        console.error("Error fetching Business Transaction setting:", error);
      }
    };

    fetchBusinessTransactionSetting();
  }, []);

  // Fetch Roles
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const resp = await httpRequest.get(`${process.env.REACT_APP_BACKEND_URL}/get-roles`);
        setRoles(resp.data.roles);
      } catch (error) {
        console.error("Error fetching roles:", error);
      }
    };

    fetchRoles();
  }, [refresh]);

  // Fetch DMS Settings
  useEffect(() => {
    const fetchDmsSettings = async () => {
      try {
        const resp = await httpRequest.get(`${process.env.REACT_APP_BACKEND_URL}/dms-settings`);
        setDmsUrl(resp.data.dms_url || '');
        setApiKey(resp.data.api_key || '');
      } catch (error) {
        console.error("Error fetching DMS settings:", error);
      }
    };

    fetchDmsSettings();
  }, []);

  const handleRefresh = () => {
    setRefresh(prevRefresh => !prevRefresh);
  };

  const tabs = [
    "General Settings",
    "Companies",
    "Functions",
    "Roles",
    "Mailbox",
    "Positions",
    "Employees",
    "Expense Categories",
    "Approval Workflow",
    "DMS Integration",
  ];

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const tabParam = queryParams.get("tab");
    if (tabParam) {
      const tabIndex = parseInt(tabParam, 10);
      if (!isNaN(tabIndex) && tabIndex >= 0 && tabIndex < tabs.length) {
        setTabIndex(tabIndex);
      }
    }
  }, [location.search]);

  const handleQuickScanToggle = async (event) => {
    const newQuickScanValue = event.target.checked;
    setQuickScan(newQuickScanValue);
    try {
      await httpRequest.post(`${process.env.REACT_APP_BACKEND_URL}/set_quick_scan`, { value: newQuickScanValue.toString() });
      enqueueSnackbar("Quick Scan setting updated successfully", { variant: "success" });
    } catch (error) {
      console.error("Error updating Quick Scan setting:", error);
      enqueueSnackbar("Failed to update Quick Scan setting", { variant: "error" });
    }
  };

  const handleBusinessTransactionToggle = (event) => {
    const newValue = event.target.checked;
    if (newValue) {
      setNewBusinessTransactionValue(newValue);
      setDialogOpen(true);
    } else {
      setBusinessTransaction(false);
      updateBusinessTransactionSetting(newValue);
    }
  };

  const updateBusinessTransactionSetting = async (value) => {
    try {
      await httpRequest.post(`${process.env.REACT_APP_BACKEND_URL}/set_business_transaction`, { value: value.toString() });
      enqueueSnackbar("Business Transaction setting updated successfully", { variant: "success" });
    } catch (error) {
      console.error("Error updating Business Transaction setting:", error);
      enqueueSnackbar("Failed to update Business Transaction setting", { variant: "error" });
    }
  };

  const handleDialogClose = (shouldContinue) => {
    setDialogOpen(false);
    if (shouldContinue) {
      setBusinessTransaction(newBusinessTransactionValue);
      updateBusinessTransactionSetting(newBusinessTransactionValue);
    } else {
      setNewBusinessTransactionValue(false);
    }
  };

  // DMS Settings Handlers
  const handleSaveDmsSettings = async () => {
    try {
      await httpRequest.post(`${process.env.REACT_APP_BACKEND_URL}/dms-settings`, {
        dms_url: dmsUrl,
        api_key: apiKey,
      });
      enqueueSnackbar("DMS settings saved successfully", { variant: "success" });
    } catch (error) {
      console.error("Error saving DMS settings:", error);
      enqueueSnackbar(error.response?.data?.error || "Failed to save DMS settings", { variant: "error" });
    }
  };

  const handleUpdateDmsSettings = async () => {
    try {
      await httpRequest.put(`${process.env.REACT_APP_BACKEND_URL}/dms-settings`, {
        dms_url: dmsUrl,
        api_key: apiKey,
      });
      enqueueSnackbar("DMS settings updated successfully", { variant: "success" });
    } catch (error) {
      console.error("Error updating DMS settings:", error);
      enqueueSnackbar(error.response?.data?.error || "Failed to update DMS settings", { variant: "error" });
    }
  };

  const handleDeleteDmsSettings = async () => {
    try {
      await httpRequest.delete(`${process.env.REACT_APP_BACKEND_URL}/dms-settings`);
      setDmsUrl('');
      setApiKey('');
      enqueueSnackbar("DMS settings deleted successfully", { variant: "success" });
    } catch (error) {
      console.error("Error deleting DMS settings:", error);
      enqueueSnackbar(error.response?.data?.error || "Failed to delete DMS settings", { variant: "error" });
    }
  };

  const activeTabTitle = tabs[tabIndex];
  return (
    <AppLayout title={activeTabTitle}>
      <Container maxWidth="xl" style={{ marginTop: "20px", marginBottom: "20px" }}>
        {tabIndex === 0 && (
          <>
            <Card header={<CardHeader titleText="System Settings" />}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 20px" }}>
                <div>Skip Pre-processing and Adjustments</div>
                <Tooltip title="Enable or disable Quick Scan">
                  <Switch
                    checked={quickScan}
                    onChange={handleQuickScanToggle}
                  />
                </Tooltip>
              </div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 20px" }}>
                <div>Enable Business Transaction Audits</div>
                <Tooltip title="Enable or disable Business Transaction Logging">
                  <Switch
                    checked={businessTransaction}
                    onChange={handleBusinessTransactionToggle}
                  />
                </Tooltip>
              </div>
            </Card>
            <SmtpCard />
            <Card header={<CardHeader titleText="Invoice Template Setup" subtitleText="Customize IDP Extraction Prompts" />}>
              <TemplatesCard />
            </Card>
          </>
        )}
        
        {tabIndex === 1 && <CompanyTable />}
        {tabIndex === 2 && <FunctionsCard />}
        {tabIndex === 3 && <RolesCard onRolesChange={handleRefresh} />}
        {tabIndex === 4 && <MailboxCard />}
        {tabIndex === 5 && <PositionsCard />}
        {tabIndex === 6 && <EmployeesCard />}
        {tabIndex === 7 && <CategoriesCard />}
        {tabIndex === 8 && <ExpenseHierarchyCard />}
        
        {tabIndex === 9 && (
          <Card header={<CardHeader titleText="SmartDMS Integration" subtitleText="Configure Document Management System" />}>
            <div style={{ padding: "20px" }}>
              <TextField
                label="API URL"
                value={dmsUrl}
                onChange={(e) => setDmsUrl(e.target.value)}
                fullWidth
                margin="normal"
                placeholder="e.g., https://dms.example.com/api"
              />
              <TextField
                label="API Key"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                fullWidth
                margin="normal"
                placeholder="Enter DMS API key"
              />
              <div style={{ marginTop: "20px" }}>
  <ButtonContained
    onClick={async () => {
      try {
        if (dmsUrl && apiKey) {
          await httpRequest.post(`${process.env.REACT_APP_BACKEND_URL}/dms-settings`, {
            dms_url: dmsUrl,
            api_key: apiKey,
          });
          enqueueSnackbar("DMS settings saved successfully", { variant: "success" });
        } else {
          await httpRequest.put(`${process.env.REACT_APP_BACKEND_URL}/dms-settings`, {
            dms_url: dmsUrl,
            api_key: apiKey,
          });
          enqueueSnackbar("DMS settings updated successfully", { variant: "success" });
        }
      } catch (error) {
        console.error("Error saving/updating DMS settings:", error);
        enqueueSnackbar(error.response?.data?.error || "Failed to save/update DMS settings", { variant: "error" });
      }
    }}
    style={{ marginRight: "10px" }}
  >
    Save
  </ButtonContained>
  
  <ButtonOutlined
    onClick={async () => {
      try {
        await httpRequest.delete(`${process.env.REACT_APP_BACKEND_URL}/dms-settings`);
        setDmsUrl('');
        setApiKey('');
        enqueueSnackbar("DMS settings deleted successfully", { variant: "success" });
      } catch (error) {
        console.error("Error deleting DMS settings:", error);
        enqueueSnackbar(error.response?.data?.error || "Failed to delete DMS settings", { variant: "error" });
      }
    }}
  >
    Delete
  </ButtonOutlined>
</div>

            </div>
          </Card>
        )}

        <Dialog open={dialogOpen} onClose={() => handleDialogClose(false)}>
          <DialogTitle>Confirm Action</DialogTitle>
          <DialogContent>
            <div>
              Enabling audits for business transactions may degrade performance. It is not recommended to enable it for a longer period of time.
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => handleDialogClose(false)} color="primary">
              Cancel
            </Button>
            <Button onClick={() => handleDialogClose(true)} color="primary">
              Continue Anyway
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </AppLayout>
  );
};

export default SettingsPage;